import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { Building2, Plus, Loader2, Unplug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useZaloAccounts,
  useConnectZaloOa,
  useDeleteZaloAccount,
  useZaloOaConfig,
} from '@/hooks/use-integrations'
import {
  SelectableTable,
  accountColumns,
  accountSearchText,
  ACCOUNT_FILTERS,
  BulkDisconnectButton,
} from './accounts-table'
import { noConnectHint, useAutoAdd } from './channel-catalog'
import { useMyPermissions } from '@/hooks/use-settings'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { OAUTH_RETURN_TO } from '@/lib/onboarding'
import { ChannelSetupGuide } from './channel-setup-guide'
import type { OaConfigStatus } from '@/hooks/use-integrations'


/** Hiện khi backend chưa có khoá ứng dụng Zalo OA. */
function OaSetupGuide({ cfg }: { cfg: OaConfigStatus }) {
  const { t } = useTranslation('chanZalo')
  return (
    <ChannelSetupGuide
      title={cfg.hostMismatch ? t('oa.guide.mismatchTitle') : t('oa.guide.notConfiguredTitle')}
      intro={
        cfg.hostMismatch
          ? t('oa.guide.mismatchIntro', { redirectUri: cfg.redirectUri, apiBase: cfg.apiBase })
          : t('oa.guide.notConfiguredIntro')
      }
      steps={[
        <Trans
          t={t}
          i18nKey="oa.guide.step1"
          components={{
            link: <a href="https://developers.zalo.me/createapp" target="_blank" rel="noreferrer" className="font-medium text-primary underline" />,
          }}
        />,
        <Trans t={t} i18nKey="oa.guide.step2" components={{ b: <strong /> }} />,
        <Trans t={t} i18nKey="oa.guide.step3" components={{ b: <strong />, code: <code className="rounded bg-background px-1" /> }} />,
      ]}
      copyItems={[
        { label: t('oa.guide.callbackLabel'), value: cfg.redirectUri },
        { label: t('oa.guide.webhookLabel'), value: cfg.webhookUrl },
      ]}
      missing={cfg.missing.map((k) => `${k}=`)}
      footnote={
        <>
          <Trans t={t} i18nKey="oa.guide.footnote" components={{ b: <strong />, code: <code className="rounded bg-background px-1" /> }} />
          {!cfg.webhookStrict && t('oa.guide.footnoteLoose')}
        </>
      }
    />
  )
}

export function ZaloOaTab() {
  const { t } = useTranslation('chanZalo')
  const { data, isLoading, isError } = useZaloAccounts('oa')
  const { data: cfg } = useZaloOaConfig()
  const connect = useConnectZaloOa()
  const remove = useDeleteZaloAccount()
  // Kết nối OA = create, ngắt = delete — thiếu quyền thì không hiện nút.
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('integrations.create')
  const canDelete = !!perms?.has('integrations.delete')

  // Mở từ trang Bắt đầu → callback OAuth đưa về /onboarding (returnTo lưu trong state phía server).
  const onboardingReturn = useOnboardingReturn()
  const handleConnect = () => {
    connect.mutate(onboardingReturn.active ? { returnTo: OAUTH_RETURN_TO } : undefined, {
      onSuccess: (url) => {
        // Redirect sang trang uỷ quyền OAuth của Zalo
        window.location.href = url
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  useAutoAdd(handleConnect, cfg?.configured === true)

  const handleDisconnect = (id: string) => {
    remove.mutate(id, {
      onSuccess: () => toast.success(t('oa.disconnected')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('oa.intro')}
        </p>
        {canCreate && (
          <Button onClick={handleConnect} disabled={connect.isPending || cfg?.configured === false}>
            {connect.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
            {t('oa.connect')}
          </Button>
        )}
      </div>

      {cfg && !cfg.configured && <OaSetupGuide cfg={cfg} />}

      {/* Kiểu hỏng âm thầm nhất của webhook: Zalo vẫn gửi, mình vẫn trả 200,
          nhưng bỏ hết vì chữ ký không khớp. Không hiện con số này thì nhìn
          ngoài y hệt "khách chưa nhắn gì". */}
      {cfg && cfg.unverifiedWebhookEvents > 0 && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <p className="font-semibold">
            {t('oa.unverified.title', { count: cfg.unverifiedWebhookEvents })}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            <Trans t={t} i18nKey="oa.unverified.desc" components={{ code: <code className="rounded bg-background px-1" /> }} />
          </p>
        </div>
      )}

      <SelectableTable
        rows={data}
        rowKey={(a) => a.id}
        loading={isLoading}
        error={isError}
        filters={ACCOUNT_FILTERS}
        searchText={accountSearchText}
        empty={
          <EmptyState
            icon={Building2}
            title={t('oa.emptyTitle')}
            description={canCreate ? t('oa.emptyDesc') : noConnectHint()}
          />
        }
        columns={accountColumns({
          fallbackName: 'Zalo OA',
          sub: (acc) => (acc.externalPageId ? t('oa.oaId', { id: acc.externalPageId }) : undefined),
          actions: canDelete
            ? (acc) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDisconnect(acc.id)}
                  disabled={remove.isPending}
                  title={t('oa.disconnect')}
                >
                  <Unplug />
                </Button>
              )
            : undefined,
        })}
        bulkActions={(selected, clear) => <BulkDisconnectButton selected={selected} onDone={clear} />}
      />
    </div>
  )
}
